const React = require("react");
const Bootstrap = require("react-bootstrap");
const Modal = Bootstrap.Modal || Bootstrap.default?.Modal;
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { Button } = require("./ui");
const h = React.createElement;

const flattenCategories = (tree = []) => tree.flatMap((level1) => [
  { id: level1.id, name: level1.name },
  ...(level1.children || []).flatMap((level2) => [
    { id: level2.id, name: `${level1.name} › ${level2.name}` },
    ...(level2.children || []).map((level3) => ({ id: level3.id, name: `${level1.name} › ${level2.name} › ${level3.name}` })),
  ]),
]).filter((item) => item.id);

const moveItem = (items, index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next.map((item, priority) => ({ ...item, priority }));
};

const CollectionRow = ({ item, kind, api, t, reload, setError, runMutation }) => {
  const update = kind === "group" ? api.updateProductGroup : api.updatePersonalSupplier;
  const remove = kind === "group" ? api.deleteProductGroup : api.deletePersonalSupplier;
  const [priority, setPriority] = React.useState(item.priority || 0);
  const [productCode, setProductCode] = React.useState("");
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({ name: item.name || "", description: item.description || "", cjSupplierId: item.cjSupplierId || "" });
  React.useEffect(() => setDraft({ name: item.name || "", description: item.description || "", cjSupplierId: item.cjSupplierId || "" }), [item]);
  const mutate = async (payload, afterSuccess) => {
    setError("");
    return runMutation(() => update(item._id, payload), async () => {
      await reload();
      afterSuccess?.();
    });
  };
  const saveEdit = async () => {
    if (!draft.name.trim()) return;
    await mutate({
      name: draft.name.trim(), description: draft.description.trim(),
      ...(kind === "supplier" ? { cjSupplierId: draft.cjSupplierId.trim() } : {}),
    }, () => setEditing(false));
  };
  const destroy = async () => {
    if (!window.confirm(t("h_cj_default_delete_confirm", "Are you sure you want to delete this item?"))) return;
    await runMutation(() => remove(item._id), async () => { await reload(); });
  };
  const addProduct = async () => {
    const value = productCode.trim();
    if (!value) return;
    const looksLikePid = /^[a-f0-9-]{20,}$/i.test(value);
    const method = kind === "group" ? api.addProductGroupProduct : api.addPersonalSupplierProduct;
    await runMutation(
      () => method(item._id, looksLikePid ? { cjPid: value } : { cjSku: value }),
      async () => { setProductCode(""); await reload(); }
    );
  };
  const removeProduct = async (productId) => {
    const method = kind === "group" ? api.removeProductGroupProduct : api.removePersonalSupplierProduct;
    await runMutation(() => method(item._id, productId), async () => { await reload(); });
  };
  return h("div", { className: "cjds-default-row" },
    h("div", { className: "cjds-default-row-main" },
      h("strong", null, item.name),
      h("span", { className: "cjds-muted" }, `${(item.products || []).length} ${t("h_cj_products_count", "Products")}`)
    ),
    h("label", { className: "cjds-default-check" },
      h("input", { type: "checkbox", checked: item.active !== false, onChange: () => mutate({ active: item.active === false }) }),
      t("h_cj_default_active", "Active")
    ),
    h("label", { className: "cjds-default-check" },
      h("input", { type: "checkbox", checked: item.includeInDefaultLoading === true, onChange: () => mutate({ includeInDefaultLoading: item.includeInDefaultLoading !== true }) }),
      t("h_cj_default_set", "Default")
    ),
    h("input", {
      className: "cjds-input cjds-priority-input", type: "number", value: priority,
      "aria-label": t("h_cj_default_priority", "Priority"),
      onChange: (event) => setPriority(event.target.value), onBlur: () => mutate({ priority: Number(priority) || 0 }),
    }),
    h(Button, { variant: "secondary", onClick: () => setEditing((value) => !value) }, t("h_cj_default_edit", "Edit")),
    h(Button, { variant: "danger", onClick: destroy }, t("h_cj_default_delete", "Delete")),
    editing ? h("div", { className: "cjds-default-products cjds-default-edit-form" },
      h("input", { className: "cjds-input", value: draft.name, onChange: (event) => setDraft({ ...draft, name: event.target.value }), placeholder: t("h_cj_default_name_prompt", "Name") }),
      h("textarea", { className: "cjds-input", value: draft.description, onChange: (event) => setDraft({ ...draft, description: event.target.value }), placeholder: t("h_cj_default_description", "Description") }),
      kind === "supplier" ? h("input", { className: "cjds-input", value: draft.cjSupplierId, onChange: (event) => setDraft({ ...draft, cjSupplierId: event.target.value }), placeholder: t("h_cj_default_cj_supplier_id", "CJ Supplier ID (optional)") }) : null,
      h("div", { className: "cjds-row" },
        h(Button, { onClick: saveEdit, disabled: !draft.name.trim() }, t("h_cj_default_save", "Save")),
        h(Button, { variant: "secondary", onClick: () => setEditing(false) }, t("h_cj_default_cancel", "Cancel"))
      )
    ) : null,
    h("div", { className: "cjds-default-products" },
      h("div", { className: "cjds-row" },
        h("input", { className: "cjds-input", value: productCode, onChange: (event) => setProductCode(event.target.value), placeholder: t("h_cj_default_pid_sku", "CJ PID or SKU") }),
        h(Button, { variant: "secondary", onClick: addProduct, disabled: !productCode.trim() }, t("h_cj_default_add_product", "Add Product"))
      ),
      ...(item.products || []).map((product, productIndex) => h("div", { className: "cjds-default-product-chip", key: product._id },
        h("input", { type: "checkbox", checked: product.active !== false, onChange: () => mutate({ products: item.products.map((entry, index) => index === productIndex ? { ...entry, active: entry.active === false } : entry) }), "aria-label": t("h_cj_default_active", "Active") }),
        h("span", null, product.name || product.cjPid || product.cjSku),
        h("button", { type: "button", disabled: productIndex === 0, onClick: () => mutate({ products: moveItem(item.products, productIndex, -1) }), "aria-label": t("h_cj_default_priority", "Priority") }, "↑"),
        h("button", { type: "button", disabled: productIndex === item.products.length - 1, onClick: () => mutate({ products: moveItem(item.products, productIndex, 1) }), "aria-label": t("h_cj_default_priority", "Priority") }, "↓"),
        h("button", { type: "button", onClick: () => removeProduct(product._id), "aria-label": t("h_cj_default_remove", "Remove") }, "×")
      ))
    )
  );
};

const DefaultLoadingManager = ({ show, onHide, onChanged }) => {
  const { api, t } = useCJDropshipping();
  const [data, setData] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [productInput, setProductInput] = React.useState("");
  const [categoryId, setCategoryId] = React.useState("");
  const [newGroup, setNewGroup] = React.useState("");
  const [newGroupDescription, setNewGroupDescription] = React.useState("");
  const [newSupplier, setNewSupplier] = React.useState("");
  const [newSupplierDescription, setNewSupplierDescription] = React.useState("");
  const [newSupplierCjId, setNewSupplierCjId] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmationBusy, setConfirmationBusy] = React.useState(false);
  const [pendingMutation, setPendingMutation] = React.useState(null);

  const runMutation = React.useCallback(async (action, onSuccess = async () => {}) => {
    setError("");
    try {
      const result = await action();
      await onSuccess(result);
      onChanged?.();
      return result;
    } catch (requestError) {
      if (["CJ_PASSWORD_CONFIRMATION_REQUIRED", "CJ_PASSWORD_CONFIRMATION_INVALID", "CJ_PASSWORD_CONFIRMATION_EXPIRED"].includes(requestError.code)) {
        setPendingMutation({ action, onSuccess });
        return false;
      }
      setError(requestError.message);
      return false;
    }
  }, [onChanged]);

  const confirmAndResume = async () => {
    if (!password || !pendingMutation || confirmationBusy) return;
    setConfirmationBusy(true); setError("");
    try {
      await api.confirmPassword(password);
      const result = await pendingMutation.action();
      await pendingMutation.onSuccess(result);
      onChanged?.();
      setPendingMutation(null); setPassword("");
    } catch (requestError) { setError(requestError.message); }
    finally { setConfirmationBusy(false); }
  };

  const reload = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [configuration, categoryData] = await Promise.all([api.getDefaultLoading(), api.getCategoriesNormalized()]);
      setData(configuration);
      setCategories(flattenCategories(categoryData.categories || []));
    } catch (requestError) { setError(requestError.message); }
    finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { if (show) reload(); }, [show, reload]);

  const config = data?.configuration || { products: [], categories: [] };
  const saveConfiguration = async (next) => {
    setSaving(true); setError("");
    try {
      await runMutation(() => api.updateDefaultLoading(next), (result) => setData(result));
    } finally { setSaving(false); }
  };
  const addProduct = () => {
    const value = productInput.trim();
    if (!value) return;
    const looksLikePid = /^[a-f0-9-]{20,}$/i.test(value);
    saveConfiguration({ products: [...config.products, looksLikePid ? { cjPid: value } : { cjSku: value }] });
    setProductInput("");
  };
  const addCategory = () => {
    const category = categories.find((item) => item.id === categoryId);
    if (!category || config.categories.some((item) => item.cjCategoryId === category.id)) return;
    saveConfiguration({ categories: [...config.categories, { cjCategoryId: category.id, name: category.name, productsToLoad: 20 }] });
    setCategoryId("");
  };
  const createCollection = async (kind) => {
    const name = (kind === "group" ? newGroup : newSupplier).trim();
    if (!name) return;
    setError("");
    if (kind === "group") {
      await runMutation(
        () => api.createProductGroup({ name, description: newGroupDescription.trim() }),
        async () => { setNewGroup(""); setNewGroupDescription(""); await reload(); }
      );
    } else {
      await runMutation(
        () => api.createPersonalSupplier({ name, description: newSupplierDescription.trim(), cjSupplierId: newSupplierCjId.trim() }),
        async () => { setNewSupplier(""); setNewSupplierDescription(""); setNewSupplierCjId(""); await reload(); }
      );
    }
  };

  return h(Modal, { show, onHide, size: "xl", centered: true, className: "cjds-modal cjds-default-modal", scrollable: true },
    h(Modal.Header, { closeButton: true }, h(Modal.Title, null, t("h_cj_default_manage", "Manage Default Loading"))),
    h(Modal.Body, null,
      error ? h("div", { className: "cjds-alert", role: "alert" }, error) : null,
      pendingMutation ? h("section", { className: "cjds-default-section cjds-confirmation-panel" },
        h("h3", null, t("h_cj_default_confirm_title", "Confirm your Elakay password")),
        h("p", { className: "cjds-muted" }, t("h_cj_default_confirm_desc", "This security confirmation is required before changing Dropshipping Out settings.")),
        h("div", { className: "cjds-row" },
          h("input", { className: "cjds-input", type: "password", autoComplete: "current-password", value: password, onChange: (event) => setPassword(event.target.value), placeholder: t("h_cj_default_password", "Elakay password"), onKeyDown: (event) => { if (event.key === "Enter") confirmAndResume(); } }),
          h(Button, { onClick: confirmAndResume, disabled: confirmationBusy || !password }, confirmationBusy ? t("h_cj_loading", "Loading…") : t("h_cj_default_confirm", "Confirm")),
          h(Button, { variant: "secondary", onClick: () => { setPendingMutation(null); setPassword(""); } }, t("h_cj_default_cancel", "Cancel"))
        )
      ) : null,
      loading || !data ? h("div", { className: "cjds-state" }, t("h_cj_loading", "Loading…")) : h(React.Fragment, null,
        h("section", { className: "cjds-default-section" },
          h("h3", null, `${t("h_cj_default_products", "Default Products")} (${config.products.length})`),
          h("div", { className: "cjds-row" },
            h("input", { className: "cjds-input", value: productInput, onChange: (event) => setProductInput(event.target.value), placeholder: t("h_cj_default_pid_sku", "CJ PID or SKU") }),
            h(Button, { onClick: addProduct, disabled: saving || !productInput.trim() }, t("h_cj_default_add_product", "Add Product"))
          ),
          ...(config.products || []).map((product, index) => h("div", { className: "cjds-default-row", key: product._id || `${product.cjPid}-${product.cjSku}-${index}` },
            h("strong", null, product.name || product.cjPid || product.cjSku),
            h("span", { className: "cjds-muted" }, product.cjPid ? `PID: ${product.cjPid}` : `SKU: ${product.cjSku}`),
            h("label", { className: "cjds-default-check" }, h("input", { type: "checkbox", checked: product.active !== false, onChange: () => saveConfiguration({ products: config.products.map((item, itemIndex) => itemIndex === index ? { ...item, active: item.active === false } : item) }) }), t("h_cj_default_active", "Active")),
            h(Button, { variant: "secondary", disabled: index === 0, onClick: () => saveConfiguration({ products: moveItem(config.products, index, -1) }) }, "↑"),
            h(Button, { variant: "secondary", disabled: index === config.products.length - 1, onClick: () => saveConfiguration({ products: moveItem(config.products, index, 1) }) }, "↓"),
            h(Button, { variant: "danger", onClick: () => saveConfiguration({ products: config.products.filter((_, itemIndex) => itemIndex !== index) }) }, t("h_cj_default_remove", "Remove"))
          ))
        ),
        h("section", { className: "cjds-default-section" },
          h("h3", null, `${t("h_cj_default_groups", "Default Groups")} (${data.groups.length})`),
          h("div", { className: "cjds-row" },
            h("input", { className: "cjds-input", value: newGroup, onChange: (event) => setNewGroup(event.target.value), placeholder: t("h_cj_default_group_name", "Group name") }),
            h("input", { className: "cjds-input", value: newGroupDescription, onChange: (event) => setNewGroupDescription(event.target.value), placeholder: t("h_cj_default_description", "Description") }),
            h(Button, { onClick: () => createCollection("group") }, t("h_cj_default_create_group", "Create Group"))
          ),
          ...data.groups.map((item) => h(CollectionRow, { key: item._id, item, kind: "group", api, t, reload, setError, runMutation }))
        ),
        h("section", { className: "cjds-default-section" },
          h("h3", null, `${t("h_cj_default_categories", "Default Categories")} (${config.categories.length})`),
          h("div", { className: "cjds-row" },
            h("select", { className: "cjds-input", value: categoryId, onChange: (event) => setCategoryId(event.target.value) },
              h("option", { value: "" }, t("h_cj_default_choose_category", "Choose a CJ category")),
              ...categories.map((category) => h("option", { key: category.id, value: category.id }, category.name))
            ),
            h(Button, { onClick: addCategory, disabled: saving || !categoryId }, t("h_cj_default_add_category", "Add Category"))
          ),
          ...(config.categories || []).map((category, index) => h("div", { className: "cjds-default-row", key: category._id || category.cjCategoryId },
            h("strong", null, category.name || category.cjCategoryId),
            h("label", { className: "cjds-default-check" }, h("input", { type: "checkbox", checked: category.active !== false, onChange: () => saveConfiguration({ categories: config.categories.map((item, itemIndex) => itemIndex === index ? { ...item, active: item.active === false } : item) }) }), t("h_cj_default_active", "Active")),
            h("label", { className: "cjds-default-check" }, t("h_cj_default_products_to_load", "Products to load"),
              h("input", { className: "cjds-input cjds-priority-input", type: "number", min: 1, max: 100, value: category.productsToLoad || 20,
                onChange: (event) => { const next = config.categories.map((item, i) => i === index ? { ...item, productsToLoad: Number(event.target.value) } : item); setData({ ...data, configuration: { ...config, categories: next } }); },
                onBlur: () => saveConfiguration({ categories: config.categories }) })
            ),
            h(Button, { variant: "secondary", disabled: index === 0, onClick: () => saveConfiguration({ categories: moveItem(config.categories, index, -1) }) }, "↑"),
            h(Button, { variant: "secondary", disabled: index === config.categories.length - 1, onClick: () => saveConfiguration({ categories: moveItem(config.categories, index, 1) }) }, "↓"),
            h(Button, { variant: "danger", onClick: () => saveConfiguration({ categories: config.categories.filter((_, itemIndex) => itemIndex !== index) }) }, t("h_cj_default_remove", "Remove"))
          ))
        ),
        h("section", { className: "cjds-default-section" },
          h("h3", null, `${t("h_cj_default_suppliers", "Default Suppliers")} (${data.suppliers.length})`),
          h("div", { className: "cjds-row" },
            h("input", { className: "cjds-input", value: newSupplier, onChange: (event) => setNewSupplier(event.target.value), placeholder: t("h_cj_default_supplier_name", "Supplier name") }),
            h("input", { className: "cjds-input", value: newSupplierDescription, onChange: (event) => setNewSupplierDescription(event.target.value), placeholder: t("h_cj_default_description", "Description") }),
            h("input", { className: "cjds-input", value: newSupplierCjId, onChange: (event) => setNewSupplierCjId(event.target.value), placeholder: t("h_cj_default_cj_supplier_id", "CJ Supplier ID (optional)") }),
            h(Button, { onClick: () => createCollection("supplier") }, t("h_cj_default_add_supplier", "Add Supplier"))
          ),
          ...data.suppliers.map((item) => h(CollectionRow, { key: item._id, item, kind: "supplier", api, t, reload, setError, runMutation }))
        )
      )
    ),
    h(Modal.Footer, null, h(Button, { variant: "secondary", onClick: onHide }, t("h_cj_default_close", "Close")))
  );
};

module.exports = { DefaultLoadingManager, flattenCategories, moveItem };
